import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, App, Space, Row, Col, Alert } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowLeftOutlined, HomeOutlined } from '@ant-design/icons';
import request from '../utils/request';
import { getCurrentUser } from '../utils/user';

const CommunityCards = ({ onSelect }) => {
  const { message } = App.useApp();
  const currentUser = getCurrentUser();
  const isSuperAdmin = Boolean(currentUser.isSuperAdmin);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingRecord, setEditingRecord] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await request.get('/community', { params: { page: 1, pageSize: 999 } });
      if (res.code === 200) setList(res.data.list || []);
    } catch (error) {
      message.error('获取数据失败');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await request.delete('/community/' + id);
      message.success('删除成功');
      fetchData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const openEdit = (record, e) => {
    e.stopPropagation();
    setEditingRecord(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      if (editingRecord) {
        await request.put('/community/' + editingRecord.id, values);
        message.success('更新成功');
      } else {
        await request.post('/community', values);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchData();
    } catch (error) {
      message.error(editingRecord ? '更新失败' : '创建失败');
    }
  };

  return (
    <div>
      {isSuperAdmin && (
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingRecord(null); form.resetFields(); setModalVisible(true); }}>添加小区</Button>
        </div>
      )}
      <Row gutter={[16, 16]}>
        {list.map(c => (
          <Col key={c.id} xs={24} sm={12} md={8} lg={6}>
            <Card
              hoverable
              loading={loading}
              onClick={() => onSelect(c)}
              actions={isSuperAdmin ? [
                <EditOutlined key="edit" onClick={(e) => openEdit(c, e)} />,
                <DeleteOutlined key="delete" onClick={(e) => handleDelete(c.id, e)} />,
              ] : []}
            >
              <Card.Meta
                avatar={<HomeOutlined style={{ fontSize: 24, color: '#1677ff' }} />}
                title={c.name}
                description={c.address || '暂无地址'}
              />
            </Card>
          </Col>
        ))}
      </Row>
      <Modal title={editingRecord ? '编辑小区' : '添加小区'} open={modalVisible} onCancel={() => setModalVisible(false)} footer={null}>
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item label="小区名称" name="name" rules={[{ required: true, message: '请输入小区名称' }]}>
            <Input placeholder="请输入小区名称" />
          </Form.Item>
          <Form.Item label="地址" name="address">
            <Input placeholder="请输入地址" />
          </Form.Item>
          <Form.Item label="备注" name="remark">
            <Input.TextArea rows={3} placeholder="请输入备注" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">提交</Button>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const CommunityDetail = ({ community, onBack }) => {
  const { message } = App.useApp();
  const currentUser = getCurrentUser();
  const isSuperAdmin = Boolean(currentUser.isSuperAdmin);
  const [buildings, setBuildings] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [rooms, setRooms] = useState({});

  const [bldModalVisible, setBldModalVisible] = useState(false);
  const [bldForm] = Form.useForm();
  const [editingBld, setEditingBld] = useState(null);

  const [unitModalVisible, setUnitModalVisible] = useState(false);
  const [unitForm] = Form.useForm();
  const [editingUnit, setEditingUnit] = useState(null);

  const [roomModalVisible, setRoomModalVisible] = useState(false);
  const [roomForm] = Form.useForm();
  const [editingRoom, setEditingRoom] = useState(null);
  const [roomUnitId, setRoomUnitId] = useState(null);

  const fetchBuildings = async () => {
    try {
      const res = await request.get('/building', { params: { communityId: community.id, page: 1, pageSize: 999 } });
      if (res.code === 200) setBuildings(res.data.list || []);
    } catch (error) { message.error('获取楼栋失败'); }
  };
  const fetchUnits = async (bid) => {
    try {
      const res = await request.get('/unit', { params: { buildingId: bid, page: 1, pageSize: 999 } });
      if (res.code === 200) setUnits(res.data.list || []);
    } catch (error) { message.error('获取单元失败'); }
  };
  const fetchRooms = async (uid) => {
    try {
      const res = await request.get('/room', { params: { unitId: uid, page: 1, pageSize: 999 } });
      if (res.code === 200) setRooms(prev => ({ ...prev, [uid]: res.data.list || [] }));
    } catch (error) { message.error('获取房号失败'); }
  };

  useEffect(() => { fetchBuildings(); }, [community.id]);
  useEffect(() => {
    if (selectedBuilding) { fetchUnits(selectedBuilding); setUnits([]); setRooms({}); }
    else setUnits([]);
  }, [selectedBuilding]);

  const del = async (api, id, cb) => {
    try { await request.delete(`${api}/${id}`); message.success('删除成功'); cb(); }
    catch (error) { message.error('删除失败'); }
  };

  const tb = { size: 'small', pagination: false };

  const bldCols = [
    { title: '楼栋名称', dataIndex: 'name', key: 'name' },
    { title: '备注', dataIndex: 'remark', key: 'remark' },
    { title: '操作', key: 'action', width: 200, render: function(_, r) { return (
      <Space>
        <Button type="link" size="small" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); setEditingBld(r); bldForm.setFieldsValue(r); setBldModalVisible(true); }}>编辑</Button>
        <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); del('/building', r.id, () => { fetchBuildings(); if (selectedBuilding === r.id) setSelectedBuilding(null); }); }}>删除</Button>
      </Space>
    ); }},
  ];

  const unitCols = [
    { title: '单元名称', dataIndex: 'name', key: 'name' },
    { title: '备注', dataIndex: 'remark', key: 'remark' },
    { title: '操作', key: 'action', width: 260, render: function(_, r) { return (
      <Space>
        <Button type="link" size="small" icon={<PlusOutlined />} onClick={() => { setEditingRoom(null); roomForm.resetFields(); setRoomUnitId(r.id); setRoomModalVisible(true); }}>添加房号</Button>
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { setEditingUnit(r); unitForm.setFieldsValue(r); setUnitModalVisible(true); }}>编辑</Button>
        <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => del('/unit', r.id, () => selectedBuilding && fetchUnits(selectedBuilding))}>删除</Button>
      </Space>
    ); }},
  ];

  const bldSubmit = async (vals) => {
    try {
      if (editingBld) await request.put('/building/' + editingBld.id, vals);
      else await request.post('/building', { ...vals, community_id: community.id });
      message.success(editingBld ? '更新成功' : '创建成功');
      setBldModalVisible(false); fetchBuildings();
    } catch (error) { message.error(editingBld ? '更新失败' : '创建失败'); }
  };

  const unitSubmit = async (vals) => {
    try {
      if (editingUnit) await request.put('/unit/' + editingUnit.id, vals);
      else await request.post('/unit', { ...vals, building_id: selectedBuilding });
      message.success(editingUnit ? '更新成功' : '创建成功');
      setUnitModalVisible(false);
      if (selectedBuilding) fetchUnits(selectedBuilding);
    } catch (error) { message.error(editingUnit ? '更新失败' : '创建失败'); }
  };

  const roomSubmit = async (vals) => {
    try {
      if (editingRoom) await request.put('/room/' + editingRoom.id, vals);
      else await request.post('/room', { ...vals, unit_id: roomUnitId });
      message.success(editingRoom ? '更新成功' : '创建成功');
      setRoomModalVisible(false);
      fetchRooms(roomUnitId);
    } catch (error) { message.error(editingRoom ? '更新失败' : '创建失败'); }
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        {isSuperAdmin && <Button icon={<ArrowLeftOutlined />} onClick={onBack}>返回</Button>}
        <span style={{ fontSize: 18, fontWeight: 500 }}>{community.name}</span>
      </div>

      <Row gutter={16} style={{ height: 'calc(100vh - 160px)' }}>
        <Col span={12}>
          <div style={{ background: '#fff', padding: 16, borderRadius: 6, height: '100%', overflow: 'auto' }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 500 }}>楼栋管理</span>
              {isSuperAdmin && <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => { setEditingBld(null); bldForm.resetFields(); setBldModalVisible(true); }}>添加楼栋</Button>}
            </div>
            <Table {...tb} columns={bldCols} dataSource={buildings} rowKey="id"
              onRow={(r) => ({ onClick: () => setSelectedBuilding(r.id), style: { cursor: 'pointer', background: selectedBuilding === r.id ? '#e6f4ff' : undefined } })}
            />
          </div>
        </Col>
        <Col span={12}>
          <div style={{ background: '#fff', padding: 16, borderRadius: 6, height: '100%', overflow: 'auto' }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 500 }}>
                单元管理
                {selectedBuilding && <span style={{ fontWeight: 'normal', color: '#999', marginLeft: 8 }}>
                  （{buildings.find(b => b.id === selectedBuilding)?.name || ''}）
                </span>}
              </span>
              {selectedBuilding && isSuperAdmin && <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => { setEditingUnit(null); unitForm.resetFields(); setUnitModalVisible(true); }}>添加单元</Button>}
            </div>
            {selectedBuilding ? (
              <Table {...tb} columns={unitCols} dataSource={units} rowKey="id"
                expandable={{
                  expandedRowRender: (u) => {
                    if (!rooms[u.id]) fetchRooms(u.id);
                    return (
                      <div style={{ padding: '8px 0' }}>
                        <div style={{ marginBottom: 8 }}>
                          {isSuperAdmin && <Button type="link" size="small" icon={<PlusOutlined />} onClick={() => { setEditingRoom(null); roomForm.resetFields(); setRoomUnitId(u.id); setRoomModalVisible(true); }}>添加房号</Button>}
                        </div>
                        <Table
                          columns={[
                            { title: '房号', dataIndex: 'number', key: 'number', width: 80 },
                            { title: '备注', dataIndex: 'remark', key: 'remark' },
                            { title: '操作', key: 'action', width: 180, render: function(_, r) { return (
                              <Space>
                                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { setEditingRoom(r); roomForm.setFieldsValue(r); setRoomUnitId(u.id); setRoomModalVisible(true); }}>编辑</Button>
                                <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => del('/room', r.id, () => fetchRooms(u.id))}>删除</Button>
                              </Space>
                            ); }},
                          ]}
                          dataSource={rooms[u.id] || []}
                          rowKey="id"
                          size="small"
                          pagination={false}
                        />
                      </div>
                    );
                  },
                  rowExpandable: () => true,
                }}
              />
            ) : (
              <div style={{ color: '#999', textAlign: 'center', padding: 60 }}>请先在左侧选择一个楼栋</div>
            )}
          </div>
        </Col>
      </Row>

      <Modal title={editingBld ? '编辑楼栋' : '添加楼栋'} open={bldModalVisible} onCancel={() => setBldModalVisible(false)} footer={null}>
        <Form form={bldForm} onFinish={bldSubmit} layout="vertical">
          <Form.Item label="楼栋名称" name="name" rules={[{ required: true, message: '请输入楼栋名称' }]}><Input /></Form.Item>
          <Form.Item label="备注" name="remark"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item><Space><Button type="primary" htmlType="submit">提交</Button><Button onClick={() => setBldModalVisible(false)}>取消</Button></Space></Form.Item>
        </Form>
      </Modal>

      <Modal title={editingUnit ? '编辑单元' : '添加单元'} open={unitModalVisible} onCancel={() => setUnitModalVisible(false)} footer={null}>
        <Form form={unitForm} onFinish={unitSubmit} layout="vertical">
          <Form.Item label="单元名称" name="name" rules={[{ required: true, message: '请输入单元名称' }]}><Input /></Form.Item>
          <Form.Item label="备注" name="remark"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item><Space><Button type="primary" htmlType="submit">提交</Button><Button onClick={() => setUnitModalVisible(false)}>取消</Button></Space></Form.Item>
        </Form>
      </Modal>

      <Modal title={editingRoom ? '编辑房号' : '添加房号'} open={roomModalVisible} onCancel={() => setRoomModalVisible(false)} footer={null}>
        <Form form={roomForm} onFinish={roomSubmit} layout="vertical">
          <Form.Item label="房号" name="number" rules={[{ required: true, message: '请输入房号' }]}><Input /></Form.Item>
          <Form.Item label="备注" name="remark"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item><Space><Button type="primary" htmlType="submit">提交</Button><Button onClick={() => setRoomModalVisible(false)}>取消</Button></Space></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const PropertyStructure = () => {
  const currentUser = getCurrentUser();
  const isSuperAdmin = Boolean(currentUser.isSuperAdmin);
  const fixedCommunityId = !isSuperAdmin ? currentUser.communityId : null;
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [myCommunity, setMyCommunity] = useState(null);
  const [communityLoadError, setCommunityLoadError] = useState(null);

  useEffect(() => {
    if (fixedCommunityId && !myCommunity) {
      (async () => {
        try {
          const res = await request.get('/community/' + fixedCommunityId);
          if (res.code === 200) setMyCommunity(res.data);
          else setCommunityLoadError(res.message || '加载小区失败');
        } catch (error) {
          setCommunityLoadError(error.message || '加载小区失败');
        }
      })();
    }
  }, [fixedCommunityId]);

  if (fixedCommunityId) {
    if (communityLoadError) {
      return <Alert type="error" showIcon message="加载小区失败" description={communityLoadError} />;
    }
    if (!myCommunity) return <div style={{ padding: 24 }}>加载中...</div>;
    return <CommunityDetail community={myCommunity} onBack={() => { setSelectedCommunity(null); }} />;
  }

  if (selectedCommunity) {
    return <CommunityDetail community={selectedCommunity} onBack={() => { setSelectedCommunity(null); }} />;
  }

  return <CommunityCards onSelect={setSelectedCommunity} />;
};

export default PropertyStructure;
